import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Mail, Key, CheckCircle2, AlertCircle, Eye, EyeOff, 
  Send, Shield, Cloud, Settings, ExternalLink
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const EMAIL_PROVIDERS = [
  { 
    id: 'sendgrid', 
    name: 'SendGrid', 
    icon: Send,
    description: 'Popular email delivery service by Twilio',
    docsUrl: 'https://sendgrid.com/docs/for-developers/sending-email/api-getting-started/'
  },
  { 
    id: 'resend', 
    name: 'Resend', 
    icon: Mail,
    description: 'Modern email API for developers',
    docsUrl: 'https://resend.com/docs/introduction'
  },
  { 
    id: 'aws_ses', 
    name: 'AWS SES', 
    icon: Cloud,
    description: 'Amazon Simple Email Service',
    docsUrl: 'https://docs.aws.amazon.com/ses/latest/dg/send-email.html'
  },
];

const PlatformEmailSettingsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  
  const [settings, setSettings] = useState({
    enabled_providers: [],
    default_provider: '',
    sendgrid_api_key: '',
    resend_api_key: '',
    aws_ses_access_key: '',
    aws_ses_secret_key: '',
    aws_ses_region: 'us-east-1',
    default_from_email: '',
    default_from_name: 'effyRealty',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/email-settings/platform`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings({
          enabled_providers: data.enabled_providers || [],
          default_provider: data.default_provider || '',
          sendgrid_api_key: data.sendgrid_api_key || '',
          resend_api_key: data.resend_api_key || '',
          aws_ses_access_key: data.aws_ses_access_key || '',
          aws_ses_secret_key: data.aws_ses_secret_key || '',
          aws_ses_region: data.aws_ses_region || 'us-east-1',
          default_from_email: data.default_from_email || '',
          default_from_name: data.default_from_name || 'effyRealty',
        });
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/email-settings/platform`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success('Platform email settings saved!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleProvider = (providerId) => {
    const newEnabled = settings.enabled_providers.includes(providerId)
      ? settings.enabled_providers.filter(p => p !== providerId)
      : [...settings.enabled_providers, providerId];
    
    setSettings(prev => ({
      ...prev,
      enabled_providers: newEnabled,
      // Clear default if disabled
      default_provider: !newEnabled.includes(prev.default_provider) ? '' : prev.default_provider
    }));
  };

  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const maskKey = (key) => {
    if (!key || key.length < 10) return key;
    return key.slice(0, 6) + '...' + key.slice(-4);
  };

  if (!hasRole(['super_admin'])) {
    return (
      <div className="p-4 md:p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This page is only accessible to Super Admins.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-amber-600" />
            Platform Email Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure email providers available to all builders
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Enable Providers */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Available Email Providers</CardTitle>
          <CardDescription>
            Enable providers that builders can use. You can set platform-wide default credentials.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {EMAIL_PROVIDERS.map((provider) => {
            const isEnabled = settings.enabled_providers.includes(provider.id);
            const Icon = provider.icon;
            
            return (
              <div 
                key={provider.id}
                className={`p-4 rounded-lg border transition-all ${
                  isEnabled ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${isEnabled ? 'bg-amber-100' : 'bg-gray-100'}`}>
                      <Icon className={`h-5 w-5 ${isEnabled ? 'text-amber-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{provider.name}</h3>
                        {isEnabled && <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">Enabled</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{provider.description}</p>
                      <a 
                        href={provider.docsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                      >
                        Documentation <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => toggleProvider(provider.id)}
                  />
                </div>

                {/* Provider-specific credentials */}
                {isEnabled && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {provider.id === 'sendgrid' && (
                      <div className="space-y-2">
                        <Label className="text-sm">SendGrid API Key (Platform Default)</Label>
                        <div className="relative">
                          <Input
                            type={showKeys.sendgrid ? 'text' : 'password'}
                            value={settings.sendgrid_api_key}
                            onChange={(e) => setSettings(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                            placeholder="SG.xxxxxxxx"
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                            onClick={() => toggleShowKey('sendgrid')}
                          >
                            {showKeys.sendgrid ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    )}

                    {provider.id === 'resend' && (
                      <div className="space-y-2">
                        <Label className="text-sm">Resend API Key (Platform Default)</Label>
                        <div className="relative">
                          <Input
                            type={showKeys.resend ? 'text' : 'password'}
                            value={settings.resend_api_key}
                            onChange={(e) => setSettings(prev => ({ ...prev, resend_api_key: e.target.value }))}
                            placeholder="re_xxxxxxxx"
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                            onClick={() => toggleShowKey('resend')}
                          >
                            {showKeys.resend ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    )}

                    {provider.id === 'aws_ses' && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Access Key ID</Label>
                          <Input
                            type={showKeys.aws_access ? 'text' : 'password'}
                            value={settings.aws_ses_access_key}
                            onChange={(e) => setSettings(prev => ({ ...prev, aws_ses_access_key: e.target.value }))}
                            placeholder="AKIAXXXXXXXX"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Secret Access Key</Label>
                          <div className="relative">
                            <Input
                              type={showKeys.aws_secret ? 'text' : 'password'}
                              value={settings.aws_ses_secret_key}
                              onChange={(e) => setSettings(prev => ({ ...prev, aws_ses_secret_key: e.target.value }))}
                              placeholder="xxxxxxxx"
                              className="pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                              onClick={() => toggleShowKey('aws_secret')}
                            >
                              {showKeys.aws_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label className="text-sm">AWS Region</Label>
                          <Select 
                            value={settings.aws_ses_region} 
                            onValueChange={(v) => setSettings(prev => ({ ...prev, aws_ses_region: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                              <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                              <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                              <SelectItem value="ap-south-1">Asia Pacific (Mumbai)</SelectItem>
                              <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Default Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Default Settings</CardTitle>
          <CardDescription>
            Platform-wide defaults. Builders can override these with their own settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Email Provider</Label>
              <Select 
                value={settings.default_provider} 
                onValueChange={(v) => setSettings(prev => ({ ...prev, default_provider: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select default provider" />
                </SelectTrigger>
                <SelectContent>
                  {settings.enabled_providers.map(p => (
                    <SelectItem key={p} value={p}>
                      {EMAIL_PROVIDERS.find(ep => ep.id === p)?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default From Email</Label>
              <Input
                type="email"
                value={settings.default_from_email}
                onChange={(e) => setSettings(prev => ({ ...prev, default_from_email: e.target.value }))}
                placeholder="noreply@yourdomain.com"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Default From Name</Label>
              <Input
                value={settings.default_from_name}
                onChange={(e) => setSettings(prev => ({ ...prev, default_from_name: e.target.value }))}
                placeholder="effyRealty"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status */}
      <Alert>
        <Settings className="h-4 w-4" />
        <AlertDescription>
          <strong>{settings.enabled_providers.length}</strong> provider(s) enabled. 
          Builders will be able to choose from these providers or use platform defaults.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default PlatformEmailSettingsPage;
